import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import QRCodeComponent from '../components/QRCode';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const form = useRef();
  
  // Phone number for the call/text button
  const phoneNumber = "253-831-0008"; // Updated with the actual phone number

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate an API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        message: ''
      });
    }, 1500); // Simulate a 1.5 second delay
  };

  return (
    <>
      <Helmet>
        <title>Get Your Cash Offer | Tacoma Off Market</title>
        <meta name="description" content="Request a no-obligation cash offer for your Tacoma house. Fast closing, no repairs needed, no fees or commissions." />
      </Helmet>
      
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8 text-green-700 text-center">Get Your Cash Offer</h1>
        
        <p className="text-lg text-center mb-8 max-w-2xl mx-auto">Fill out the form below and we'll contact you within 24 hours with a fair, no-obligation cash offer for your property.</p>
        
        {submitSuccess ? (
          <div className="max-w-lg mx-auto bg-white p-8 rounded-lg shadow-lg mb-16 text-center">
            <div className="text-green-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-green-700 mb-4">Thanks, we will be in touch shortly!</h2>
            <p className="mb-6">We've received your information and will contact you within 24 hours with your cash offer.</p>
            <button 
              onClick={() => setSubmitSuccess(false)}
              className="px-6 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 transition duration-300"
            >
              Submit Another Request
            </button>
          </div>
        ) : (
          <form ref={form} onSubmit={handleSubmit} className="max-w-lg mx-auto bg-white p-8 rounded-lg shadow-lg mb-16">
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="name">Name*</label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="email">Email*</label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="phone">Phone*</label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="address">Property Address*</label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                id="address"
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2" htmlFor="message">Additional Information</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                id="message"
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your situation or any special circumstances..."
              ></textarea>
            </div>
            
            {submitError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                {submitError}
              </div>
            )}
            
            <button
              type="submit"
              className={`w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md transition duration-300 shadow-md ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Get My Cash Offer'}
            </button>
            
            <p className="text-sm text-gray-600 mt-4 text-center">
              We respect your privacy. Your information will never be shared or sold.
            </p>
          </form>
        )}
        
        {/* Call/Text Button Section - Moved below the form */}
        <div className="max-w-lg mx-auto mb-16 text-center">
          <p className="text-lg mb-4">Need to speak with someone right away?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`tel:${phoneNumber}`}
              className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
              </svg>
              Call Now: {phoneNumber}
            </a>
            <a 
              href={`sms:${phoneNumber}`}
              className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
              </svg>
              Text Us
            </a>
          </div>
        </div>
        
        {/* QR Code section at the bottom */}
        <div className="mt-16 text-center">
          <div className="bg-white p-8 rounded-lg shadow-lg inline-block">
            <h3 className="text-xl font-bold text-green-700 mb-4">Scan to Visit Our Website</h3>
            <QRCodeComponent url="https://tacomaoffmarket.com" size={200} />
            <p className="mt-4 text-center text-gray-600">
              Share this QR code with anyone who might be interested in selling their house fast for cash.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default Contact; 